import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Dialog } from '@headlessui/react'
import { Captures, Devices, TriageResult, SessionIntent, TriggerSourceFilter, TriggerSource, CaptureLabelKind } from '../api'
import type { CaptureSummary, CaptureDetail, CaptureAnnotation, MediaSchema, DeviceSummary, CaptureLabelSchema } from '../api'
import { CaptureSource } from '../api'
import AppHeader from '../components/AppHeader'
import CaptureSidePanel, { type PanelMode, pctErr } from '../components/CaptureSidePanel'
import { commonName } from '../speciesCommonNames'
import { MANUAL_SENTINEL, parseHashState, buildHash, buildShareHash, localDayStartSeconds, localNextDayStartSeconds } from '../lib/captureUrl'
import TriggerChip from '../components/TriggerChip'

const LB_PER_GRAM = 1 / 453.592
const IN_PER_MM   = 1 / 25.4

function summaryFromDetail(d: CaptureDetail): CaptureSummary {
  return {
    captureId: d.captureId,
    catchId: d.catchId,
    runId: d.runId,
    deviceId: d.deviceId,
    hostname: d.hostname,
    displayCode: d.displayCode,
    triggerSource: d.triggerSource,
    captureStartTimestamp: d.captureStartTimestamp,
    captureEndTimestamp: d.captureEndTimestamp,
    captureDurationSeconds: d.captureDurationSeconds,
    lengthMillimeters: d.lengthMillimeters,
    weightGrams: d.weightGrams,
    hasError: d.hasError,
    thumbnailUrl: d.thumbnailUrl,
    triageResult: d.triageResult,
    annotation: d.annotation,
    source: d.source,
    linkedCaptureId: d.linkedCaptureId,
    manualOrigin: d.manualOrigin,
    phoneLoggedAt: d.phoneLoggedAt,
    phoneGps: d.phoneGps,
    labels: d.labels,
  }
}

/** Values stacked most trusted first; each later one shows its % error from
 *  the first that has a value, red past 20%. */
function ComparisonCell({ rows, fmt }: { rows: { label: string; v: number | null }[]; fmt: (v: number) => string }) {
  const present = rows.filter((r): r is { label: string; v: number } => r.v != null)
  if (present.length === 0) return <>—</>
  const ref = present[0].v
  return (
    <div className="space-y-0.5 whitespace-nowrap">
      {present.map((r, i) => {
        const pct = ((r.v - ref) / ref) * 100
        return (
          <div key={r.label}>
            <span className="inline-block w-12 text-[9px] uppercase tracking-[0.12em] text-stone-400">{r.label}</span>
            {fmt(r.v)}
            {i > 0 && (
              <span className={`ml-1.5 text-[10px] ${Math.abs(pct) > 20 ? 'text-coral' : 'text-stone-400'}`}>
                {pctErr(r.v, ref)}
              </span>
            )}
          </div>
        )
      })}
    </div>
  )
}

function guideNum(c: CaptureSummary, field: string): number | null {
  const v = (c.annotation as Record<string, unknown> | null | undefined)?.[field]
  return typeof v === 'number' ? v : null
}

interface Props {
  onLogout: () => void
  onNavigate: (target: string) => void
  hostname?: string
  captureId?: string
  filterMode?: string
  showNoFish?: boolean
}

interface LogCaptureForm {
  nickname: string
  species: string
  weightLbs: string
  lengthIn: string
  angler: string
  guide: string
  notes: string
  time: string
  selectedHostname: string
}

type SortKey = 'triage' | 'date' | 'net' | 'weight' | 'length'
type SortDir = 'asc' | 'desc'

const TRIAGE_ORDER: Record<string, number> = { pending: 0, keep: 1, discard: 2 }

function fmtDate(ts: number | null | undefined): string {
  if (!ts) return '—'
  return new Date(ts * 1000).toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
    hour12: false,
  })
}

function captureTime(c: CaptureSummary | CaptureDetail): number | null {
  if (c.captureStartTimestamp != null) return c.captureStartTimestamp
  const ann = c.annotation as Record<string, unknown> | null | undefined
  const t = ann ? ann['user_entered_time'] : null
  return typeof t === 'number' ? t : null
}

function displayCodeStr(c: CaptureSummary): string {
  return c.displayCode ?? '--'
}

function annNicknameStr(c: CaptureSummary): string | null {
  const ann = c.annotation as Record<string, unknown> | null
  const n = ann?.nickname
  return typeof n === 'string' && n ? n : null
}

function triageSelectClass(result: TriageResult): string {
  if (result === TriageResult.Keep)
    return 'bg-pine/20 border-pine/50 text-pine font-medium'
  if (result === TriageResult.Discard)
    return 'bg-coral/10 border-coral/30 text-coral'
  return 'bg-amber-100 border-amber-400 text-amber-800 font-semibold'
}

const emptyLogForm: LogCaptureForm = {
  nickname: '', species: '', weightLbs: '', lengthIn: '',
  angler: '', guide: '', notes: '', time: '', selectedHostname: '',
}

export default function CapturesPage({ onLogout, onNavigate, hostname: initialHostname }: Props) {
  const [captures, setCaptures] = useState<CaptureSummary[]>([])
  const [devices, setDevices] = useState<DeviceSummary[]>([])
  const [details, setDetails] = useState<Record<string, CaptureDetail>>({})

  const [filterNets, setFilterNets] = useState<Set<string>>(() => {
    if (initialHostname) return new Set([initialHostname])
    return parseHashState().nets
  })
  const [filterTriage, setFilterTriage] = useState<Set<TriageResult>>(
    () => parseHashState().triage
  )
  const [filterSessionIntent, setFilterSessionIntent] = useState<Set<SessionIntent>>(
    () => parseHashState().sessionIntent
  )
  const [filterTrigger, setFilterTrigger] = useState<Set<TriggerSourceFilter>>(
    () => parseHashState().trigger
  )
  const [filterLabels, setFilterLabels] = useState<Set<string>>(() => parseHashState().labels)
  const [labelOptions, setLabelOptions] = useState<CaptureLabelSchema[]>([])
  const [filterDateFrom, setFilterDateFrom] = useState<string | null>(() => parseHashState().dateFrom)
  const [filterDateTo, setFilterDateTo] = useState<string | null>(() => parseHashState().dateTo)
  const [listTruncated, setListTruncated] = useState(false)
  // Filters can change faster than /capture/list answers (e.g. typing a date);
  // only the newest request may write state, or a stale response wins.
  const listReqRef = useRef(0)

  const [searchQuery, setSearchQuery] = useState('')
  const [netSearch, setNetSearch] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('triage')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [compact, setCompact] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)

  const [selectedCaptureId, setSelectedCaptureId] = useState<string | null>(null)
  const [panelMode, setPanelMode] = useState<PanelMode>(
    () => (window.localStorage.getItem('kodiak-capture-panel-mode') as PanelMode | null) ?? 'default'
  )
  useEffect(() => { window.localStorage.setItem('kodiak-capture-panel-mode', panelMode) }, [panelMode])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [bulkConfirm, setBulkConfirm] = useState<{ action: 'keep' | 'discard' } | null>(null)
  // Bulk catch label: the label picked in the bulk bar, and a pending add/remove to confirm.
  const [bulkLabelId, setBulkLabelId] = useState<string>('')
  const [bulkLabelConfirm, setBulkLabelConfirm] = useState<{ labelId: string; applied: boolean } | null>(null)
  const [bulkLabelError, setBulkLabelError] = useState<string | null>(null)
  const [bulkLoading, setBulkLoading] = useState(false)
  const [showLogCapture, setShowLogCapture] = useState(false)
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
  const [logForm, setLogForm] = useState<LogCaptureForm>(emptyLogForm)
  const [loggingCapture, setLoggingCapture] = useState(false)
  const [loadingCaptures, setLoadingCaptures] = useState(true)
  const [mounted, setMounted] = useState(false)

  const hasManualFilter = filterNets.has(MANUAL_SENTINEL)
  const deviceNets = useMemo(
    () => Array.from(filterNets).filter(n => n !== MANUAL_SENTINEL),
    [filterNets]
  )

  const currentHash = (captureId: string | null) =>
    buildHash({ nets: filterNets, triage: filterTriage, sessionIntent: filterSessionIntent, trigger: filterTrigger, labels: filterLabels, dateFrom: filterDateFrom, dateTo: filterDateTo, captureId })

  const loadCaptures = useCallback(async (
    nets?: Set<string>, triage?: Set<TriageResult>, sessionIntent?: Set<SessionIntent>,
    dateFrom?: string | null, dateTo?: string | null,
    trigger?: Set<TriggerSourceFilter>, labels?: Set<string>
  ) => {
    const resolvedNets = nets ?? filterNets
    const resolvedTriage = triage ?? filterTriage
    const resolvedSessionIntent = sessionIntent ?? filterSessionIntent
    const resolvedTrigger = trigger ?? filterTrigger
    const resolvedLabels = labels ?? filterLabels
    const resolvedDateFrom = dateFrom === undefined ? filterDateFrom : dateFrom
    const resolvedDateTo = dateTo === undefined ? filterDateTo : dateTo
    const hasManual = resolvedNets.has(MANUAL_SENTINEL)
    const devNets = Array.from(resolvedNets).filter(n => n !== MANUAL_SENTINEL)
    const reqId = ++listReqRef.current
    setLoadingCaptures(true)
    try {
      const resp = await Captures.listCaptures({
        listCapturesRequest: {
          includeAllDevices: resolvedNets.size === 0 || hasManual,
          includeDeviceIds: hasManual || resolvedNets.size === 0 ? [] : devNets,
          includeTriageResults: Array.from(resolvedTriage),
          includeSessionIntents: Array.from(resolvedSessionIntent),
          startTimestamp: resolvedDateFrom ? localDayStartSeconds(resolvedDateFrom) : undefined,
          endTimestamp: resolvedDateTo ? localNextDayStartSeconds(resolvedDateTo) : undefined,
          includeTriggerSources: Array.from(resolvedTrigger),
          // No labels ticked = no narrowing; an explicit empty list would match nothing.
          includeLabelIds: resolvedLabels.size > 0 ? Array.from(resolvedLabels) : undefined,
        },
      })
      if (reqId !== listReqRef.current) return
      setCaptures(resp.captures ?? [])
      setListTruncated(resp.truncated)
    } finally {
      if (reqId === listReqRef.current) setLoadingCaptures(false)
    }
  }, [filterNets, filterTriage, filterSessionIntent, filterDateFrom, filterDateTo, filterTrigger, filterLabels])

  useEffect(() => {
    Captures.listCaptureLabels({ body: {} }).then(r => setLabelOptions(r.labels)).catch(() => {})
    Devices.listDevices().then(r => setDevices(r.devices)).catch(() => {})
  }, [])

  useEffect(() => {
    const initial = parseHashState()
    if (initial.captureId) {
      // Fire detail + list in parallel — side panel renders as soon as detail returns,
      // independent of the (potentially slow) list call.
      Captures.getCaptures({ getCapturesRequest: { captureIds: [initial.captureId] } })
        .then(resp => {
          const detail = resp.captures[0]
          if (!detail) {
            // Catch missing/deleted — drop catch= and fall through to default list view.
            window.history.replaceState(null, '', currentHash(null))
            return
          }
          setDetails(prev => ({ ...prev, [detail.captureId]: detail }))
          setCaptures(prev => (prev.length === 0 ? [summaryFromDetail(detail)] : prev))
          setSelectedCaptureId(detail.captureId)
          if (initial.isLegacy) {
            window.history.replaceState(null, '', buildShareHash(summaryFromDetail(detail)))
          }
        })
        .catch(() => {})
      loadCaptures(initial.nets, initial.triage, initial.sessionIntent, initial.dateFrom, initial.dateTo, initial.trigger, initial.labels)
    } else {
      window.history.replaceState(null, '', currentHash(null))
      loadCaptures()
    }
    setMounted(true)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!mounted) return
    window.history.replaceState(null, '', currentHash(selectedCaptureId))
    loadCaptures()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterNets, filterTriage, filterSessionIntent, filterDateFrom, filterDateTo, filterTrigger, filterLabels])

  const displayedCaptures = useMemo(() => {
    // Triage is filtered server-side, but a row triaged in place (dropdown or
    // bulk action) keeps its old position until refetch unless we re-apply it.
    let result = captures
      .filter(c => !(c.source === CaptureSource.Manual && c.linkedCaptureId !== null))
      .filter(c => filterTriage.has(c.triageResult))

    if (filterNets.size > 0) {
      result = result.filter(c => {
        if (hasManualFilter && c.source === CaptureSource.Manual) return true
        if (c.hostname && deviceNets.includes(c.hostname)) return true
        return filterNets.size === 0
      })
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase()
      result = result.filter(c => {
        const haystack: string[] = [c.captureId, c.hostname ?? '']
        if (c.displayCode) haystack.push(c.displayCode)
        if (c.catchId) haystack.push(c.catchId)
        if (c.annotation) {
          for (const v of Object.values(c.annotation as Record<string, unknown>)) {
            if (typeof v === 'string') haystack.push(v)
          }
        }
        return haystack.some(s => s.toLowerCase().includes(q))
      })
    }

    return [...result].sort((a, b) => {
      let cmp = 0
      switch (sortKey) {
        case 'triage':
          cmp = (TRIAGE_ORDER[a.triageResult] ?? 99) - (TRIAGE_ORDER[b.triageResult] ?? 99)
          if (cmp === 0) cmp = (captureTime(b) ?? 0) - (captureTime(a) ?? 0)
          break
        case 'date':
          cmp = (captureTime(a) ?? 0) - (captureTime(b) ?? 0)
          break
        case 'net':
          cmp = (a.hostname ?? '').localeCompare(b.hostname ?? '')
          break
        case 'weight':
          cmp = (a.weightGrams ?? 0) - (b.weightGrams ?? 0)
          break
        case 'length':
          cmp = (a.lengthMillimeters ?? 0) - (b.lengthMillimeters ?? 0)
          break
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [captures, filterNets, hasManualFilter, deviceNets, filterTriage, sortKey, sortDir, searchQuery])

  async function loadDetail(captureId: string) {
    if (details[captureId]) return
    try {
      const resp = await Captures.getCaptures({ getCapturesRequest: { captureIds: [captureId] } })
      const detail = resp.captures[0]
      if (detail) setDetails(prev => ({ ...prev, [captureId]: detail }))
    } catch {}
  }

  function handleRowClick(captureId: string) {
    setSelectedCaptureId(captureId)
    loadDetail(captureId)
    window.history.replaceState(null, '', currentHash(captureId))
  }

  function handlePanelClose() {
    setSelectedCaptureId(null)
    window.history.replaceState(null, '', currentHash(null))
  }

  // Prev/next walks displayedCaptures (respects current filter + sort).
  const selectedIdx = selectedCaptureId
    ? displayedCaptures.findIndex(c => c.captureId === selectedCaptureId)
    : -1
  const prevDisabled = selectedIdx <= 0
  const nextDisabled = selectedIdx < 0 || selectedIdx >= displayedCaptures.length - 1
  function handlePrev() {
    if (prevDisabled) return
    handleRowClick(displayedCaptures[selectedIdx - 1].captureId)
  }
  function handleNext() {
    if (nextDisabled) return
    handleRowClick(displayedCaptures[selectedIdx + 1].captureId)
  }

  // A deep-linked capture may be absent from `captures` (e.g. a Discard catch
  // opened under the default Keep+Pending filter) — the panel then renders from
  // `details`, so optimistic edits must patch both stores or the panel won't
  // reflect them.
  function patchCapture(captureId: string, patch: Partial<Pick<CaptureSummary, 'triageResult' | 'annotation' | 'labels'>>) {
    setCaptures(prev => prev.map(x => x.captureId === captureId ? { ...x, ...patch } : x))
    setDetails(prev => prev[captureId] ? { ...prev, [captureId]: { ...prev[captureId], ...patch } } : prev)
  }

  async function handleTriageChange(captureId: string, result: TriageResult) {
    const c = captures.find(x => x.captureId === captureId) ?? details[captureId]
    if (!c) return
    const prev = c.triageResult
    patchCapture(captureId, { triageResult: result })
    try {
      await Captures.annotateCaptures({
        annotateCapturesRequest: {
          annotations: [{ captureId, triageResult: result, annotation: c.annotation ?? null }],
        },
      })
    } catch {
      patchCapture(captureId, { triageResult: prev })
    }
  }

  async function handleAnnotationSave(captureId: string, annotation: CaptureAnnotation) {
    const prev = (captures.find(x => x.captureId === captureId) ?? details[captureId])?.annotation ?? null
    patchCapture(captureId, { annotation })
    try {
      await Captures.annotateCaptures({
        annotateCapturesRequest: {
          annotations: [{ captureId, triageResult: null, annotation }],
        },
      })
    } catch {
      patchCapture(captureId, { annotation: prev })
    }
  }

  function handleMediaUploaded(captureId: string, media: MediaSchema) {
    setDetails(prev => {
      const d = prev[captureId]
      if (!d) return prev
      return { ...prev, [captureId]: { ...d, media: [...d.media, media] } }
    })
  }

  function handleLinked(manualCaptureId: string, deviceCaptureId: string) {
    setCaptures(prev => prev.filter(c => c.captureId !== manualCaptureId))
    setDetails(prev => { const next = { ...prev }; delete next[deviceCaptureId]; return next })
    setSelectedCaptureId(deviceCaptureId)
    window.history.replaceState(null, '', currentHash(deviceCaptureId))
    loadDetail(deviceCaptureId)
  }

  async function handleLogCapture() {
    setLoggingCapture(true)
    try {
      const annotation: CaptureAnnotation = {
        nickname:              logForm.nickname  || null,
        species:               logForm.species   || null,
        angler:                logForm.angler    || null,
        guide:                 logForm.guide     || null,
        notes:                 logForm.notes     || null,
        measured_weight_grams: logForm.weightLbs ? parseFloat(logForm.weightLbs) / LB_PER_GRAM : null,
        measured_length_mm:    logForm.lengthIn  ? parseFloat(logForm.lengthIn)  / IN_PER_MM   : null,
        user_entered_time:     logForm.time ? new Date(logForm.time).getTime() / 1000 : null,
        annotated_at:          Date.now() / 1000,
      } as unknown as CaptureAnnotation
      const resp = await Captures.createManualCapture({
        createManualCaptureRequest: { hostname: logForm.selectedHostname || null, annotation },
      })
      setCaptures(prev => [resp.capture, ...prev])
      setShowLogCapture(false)
      setLogForm(emptyLogForm)
    } finally {
      setLoggingCapture(false)
    }
  }

  function handleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir(key === 'triage' || key === 'date' ? 'asc' : 'desc')
    }
  }

  function toggleSelectAll() {
    const visible = displayedCaptures.map(c => c.captureId)
    const allSelected = visible.every(id => selected.has(id))
    if (allSelected) {
      setSelected(new Set())
    } else {
      setSelected(new Set(visible))
    }
  }

  function toggleSelectOne(captureId: string) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(captureId)) next.delete(captureId)
      else next.add(captureId)
      return next
    })
  }

  async function executeBulkTriage(action: 'keep' | 'discard') {
    setBulkLoading(true)
    const result = action === 'keep' ? TriageResult.Keep : TriageResult.Discard
    const ids = Array.from(selected)
    try {
      await Captures.annotateCaptures({
        annotateCapturesRequest: {
          annotations: ids.map(id => ({
            captureId: id,
            triageResult: result,
            annotation: captures.find(c => c.captureId === id)?.annotation ?? null,
          })),
        },
      })
      setCaptures(prev => prev.map(c => selected.has(c.captureId) ? { ...c, triageResult: result } : c))
      setSelected(new Set())
      // The page was capped: what was just triaged away has freed room for the
      // next batch, so fetch it rather than leaving an emptied table.
      if (listTruncated) void loadCaptures()
    } finally {
      setBulkLoading(false)
      setBulkConfirm(null)
    }
  }

  async function executeBulkLabel(labelId: string, applied: boolean) {
    setBulkLoading(true)
    setBulkLabelError(null)
    try {
      const r = await Captures.setCaptureLabel({
        setCaptureLabelRequest: { captureIds: Array.from(selected), labelId, applied },
      })
      const labelsById = new Map(r.captures.map(c => [c.captureId, c.labels ?? []]))
      setCaptures(prev => prev.map(c => labelsById.has(c.captureId) ? { ...c, labels: labelsById.get(c.captureId) } : c))
      // Selection stays, so several labels can go on the same captures in a row.
      setBulkLabelConfirm(null)
    } catch (e) {
      setBulkLabelError(String(e))
    } finally {
      setBulkLoading(false)
    }
  }
  const catchLabels = labelOptions.filter(l => l.kind === CaptureLabelKind.Catch)
  const bulkLabel = labelOptions.find(l => l.id === bulkLabelConfirm?.labelId) ?? null
  const labelText = (l: CaptureLabelSchema) => (l.category ? `${l.category}: ${l.name}` : l.name)

  const keepCount    = captures.filter(c => c.triageResult === TriageResult.Keep).length
  const pendingCount = captures.filter(c => c.triageResult === TriageResult.Pending).length
  const discardCount = captures.filter(c => c.triageResult === TriageResult.Discard).length
  const manualCount  = captures.filter(c => c.source === CaptureSource.Manual && !c.linkedCaptureId).length
  const buttonCount  = captures.filter(c => c.triggerSource === TriggerSource.Button).length
  const weightCount  = captures.filter(c => c.triggerSource === TriggerSource.Weight).length
  const apiCount     = captures.filter(c => c.triggerSource === TriggerSource.Api).length
  const noTriggerCount = captures.length - buttonCount - weightCount - apiCount
  const showManualFacet = true

  function toggleNet(hostname: string) {
    setFilterNets(prev => {
      const next = new Set(prev)
      if (next.has(hostname)) next.delete(hostname)
      else next.add(hostname)
      return next
    })
  }
  function isolateNet(hostname: string) { setFilterNets(new Set([hostname])) }
  function toggleTriage(t: TriageResult) {
    setFilterTriage(prev => {
      const next = new Set(prev)
      if (next.has(t)) next.delete(t)
      else next.add(t)
      return next
    })
  }
  function isolateTriage(t: TriageResult) { setFilterTriage(new Set([t])) }
  function toggleSessionIntent(v: SessionIntent) {
    setFilterSessionIntent(prev => {
      const next = new Set(prev)
      if (next.has(v)) next.delete(v)
      else next.add(v)
      return next
    })
  }
  function isolateSessionIntent(v: SessionIntent) { setFilterSessionIntent(new Set([v])) }
  function toggleTrigger(v: TriggerSourceFilter) {
    setFilterTrigger(prev => {
      const next = new Set(prev)
      if (next.has(v)) next.delete(v); else next.add(v)
      return next
    })
  }
  function isolateTrigger(v: TriggerSourceFilter) { setFilterTrigger(new Set([v])) }
  function toggleLabel(id: string) {
    setFilterLabels(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  // Falls back to detail-derived summary so the panel keeps rendering when filters
  // exclude the focused catch (e.g. legacy Discard URL whose initial list call used
  // the default Keep+Pending filter and didn't return the row).
  const selectedCapture = displayedCaptures.find(c => c.captureId === selectedCaptureId)
    ?? captures.find(c => c.captureId === selectedCaptureId)
    ?? (selectedCaptureId && details[selectedCaptureId] ? summaryFromDetail(details[selectedCaptureId]) : null)

  function SortHeader({ label, k, className: cls = '' }: { label: string; k: SortKey; className?: string }) {
    const active = sortKey === k
    return (
      <th
        onClick={() => handleSort(k)}
        className={`px-3 py-2.5 text-left select-none cursor-pointer group whitespace-nowrap ${cls}`}
      >
        <span className="flex items-center gap-1 font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500 group-hover:text-stone-900">
          {label}
          <svg
            className={`w-2 h-2 shrink-0 transition-all ${active ? 'opacity-100' : 'opacity-0'} ${active && sortDir === 'desc' ? 'rotate-180' : ''}`}
            viewBox="0 0 10 8" fill="currentColor"
          >
            <path d="M5 0l5 8H0z" />
          </svg>
        </span>
      </th>
    )
  }

  function FilterPanelContent() {
    return (
      <div className="space-y-5">
        <div>
          <label className="block font-mono text-[10px] tracking-[0.2em] uppercase text-stone-400 mb-1.5">
            Search
          </label>
          <div className="relative">
            <svg viewBox="0 0 16 16" className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-stone-400 pointer-events-none" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
              <circle cx="6.5" cy="6.5" r="4" />
              <path d="M10 10l3 3" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="code, net, nickname, angler, species…"
              className="w-full pl-6 pr-6 py-1.5 font-mono text-[11px] bg-stone-50 border border-stone-200
                rounded-lg text-stone-700 placeholder-stone-400
                focus:outline-none focus:border-bear/40 focus:bg-white transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-stone-400 hover:text-stone-700 cursor-pointer"
              >
                <svg viewBox="0 0 12 12" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <path d="M2 2l8 8M10 2l-8 8" />
                </svg>
              </button>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-stone-400">Date</span>
            {(filterDateFrom || filterDateTo) && (
              <button
                onClick={() => { setFilterDateFrom(null); setFilterDateTo(null) }}
                className="font-mono text-[10px] text-stone-400 hover:text-bear cursor-pointer"
              >
                Clear
              </button>
            )}
          </div>
          <div className="space-y-1">
            {([
              { label: 'From', value: filterDateFrom, set: setFilterDateFrom },
              { label: 'To',   value: filterDateTo,   set: setFilterDateTo },
            ] as const).map(({ label, value, set }) => (
              <label key={label} className="flex items-center gap-2">
                <span className="font-mono text-[10px] text-stone-400 w-8">{label}</span>
                <input
                  type="date"
                  value={value ?? ''}
                  onChange={e => set(e.target.value || null)}
                  className="flex-1 min-w-0 font-mono text-xs border border-stone-200 rounded px-2 py-1 focus:outline-none focus:border-bear"
                />
              </label>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-stone-400">
              Net
              {filterNets.size > 0 && (
                <span className="ml-1.5 bg-bear/10 text-bear px-1.5 py-0.5 rounded text-[9px]">
                  {filterNets.size}
                </span>
              )}
            </span>
            {filterNets.size > 0 && (
              <button onClick={() => setFilterNets(new Set())}
                className="font-mono text-[10px] text-stone-400 hover:text-bear cursor-pointer">
                Clear
              </button>
            )}
          </div>
          {devices.length > 5 && (
            <input
              type="text"
              value={netSearch}
              onChange={e => setNetSearch(e.target.value)}
              placeholder="filter nets…"
              className="w-full mb-1.5 px-2 py-1 font-mono text-[10px] bg-stone-50 border border-stone-200
                rounded text-stone-700 placeholder-stone-400
                focus:outline-none focus:border-bear/40 focus:bg-white transition-colors"
            />
          )}
          <div className="max-h-48 overflow-y-auto space-y-0.5 pr-1">
            {devices
              .filter(d => !netSearch.trim() || d.hostname.toLowerCase().includes(netSearch.trim().toLowerCase()))
              .map(d => (
              <div key={d.hostname} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filterNets.size === 0 || filterNets.has(d.hostname)}
                  onChange={() => toggleNet(d.hostname)}
                  className="w-3.5 h-3.5 rounded border-stone-300 accent-bear cursor-pointer"
                />
                <button
                  onClick={() => isolateNet(d.hostname)}
                  className="flex-1 text-left font-mono text-xs text-stone-700 hover:text-bear transition-colors cursor-pointer py-0.5 truncate"
                >
                  {d.hostname}
                </button>
              </div>
            ))}
            {showManualFacet && (
              <div className="flex items-center gap-2 border-t border-stone-100 pt-1 mt-1">
                <input
                  type="checkbox"
                  checked={filterNets.size === 0 || filterNets.has(MANUAL_SENTINEL)}
                  onChange={() => toggleNet(MANUAL_SENTINEL)}
                  className="w-3.5 h-3.5 rounded border-stone-300 accent-bear cursor-pointer"
                />
                <button
                  onClick={() => isolateNet(MANUAL_SENTINEL)}
                  className="flex-1 text-left font-mono text-xs text-stone-400 italic hover:text-bear transition-colors cursor-pointer py-0.5"
                >
                  Manual
                </button>
                <span className="font-mono text-[10px] text-stone-400">{manualCount}</span>
              </div>
            )}
            {devices.length === 0 && (
              <p className="font-mono text-[10px] text-stone-400">No devices</p>
            )}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-stone-400">Status</span>
            {(filterTriage.has(TriageResult.Discard) || !filterTriage.has(TriageResult.Keep) || !filterTriage.has(TriageResult.Pending)) && (
              <button
                onClick={() => setFilterTriage(new Set([TriageResult.Keep, TriageResult.Pending]))}
                className="font-mono text-[10px] text-stone-400 hover:text-bear cursor-pointer"
              >
                Clear
              </button>
            )}
          </div>
          <div className="space-y-0.5">
            {([
              { label: 'Keep',      value: TriageResult.Keep,    count: keepCount,    dot: 'bg-pine',       text: 'text-pine font-medium' },
              { label: 'Untriaged', value: TriageResult.Pending, count: pendingCount, dot: 'bg-amber-400',  text: 'text-amber-800 font-semibold' },
              { label: 'Discard',   value: TriageResult.Discard, count: discardCount, dot: 'bg-coral',      text: 'text-coral' },
            ] as const).map(({ label, value, count, dot, text }) => (
              <div key={value} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filterTriage.has(value)}
                  onChange={() => toggleTriage(value)}
                  className="w-3.5 h-3.5 rounded border-stone-300 accent-bear cursor-pointer"
                />
                <button
                  onClick={() => isolateTriage(value)}
                  className={`flex-1 text-left font-mono text-xs hover:text-bear transition-colors cursor-pointer py-0.5 flex items-center gap-1.5 ${text}`}
                >
                  <span className={`w-2 h-2 rounded-full shrink-0 ${dot}`} />
                  {label}
                </button>
                <span className="font-mono text-[10px] text-stone-400">{count}</span>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-stone-400">Session</span>
            {filterSessionIntent.size < 3 && (
              <button
                onClick={() => setFilterSessionIntent(new Set([SessionIntent.Fishing, SessionIntent.Testing, SessionIntent.Unknown]))}
                className="font-mono text-[10px] text-stone-400 hover:text-bear cursor-pointer"
              >
                Clear
              </button>
            )}
          </div>
          <div className="space-y-0.5">
            {([
              { label: 'Fishing', value: SessionIntent.Fishing, dot: 'bg-pine',      text: 'text-pine font-medium' },
              { label: 'Testing', value: SessionIntent.Testing, dot: 'bg-amber-400', text: 'text-amber-800 font-semibold' },
              { label: 'Unknown', value: SessionIntent.Unknown, dot: 'bg-stone-300', text: 'text-stone-500' },
            ] as const).map(({ label, value, dot, text }) => (
              <div key={value} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filterSessionIntent.has(value)}
                  onChange={() => toggleSessionIntent(value)}
                  className="w-3.5 h-3.5 rounded border-stone-300 accent-bear cursor-pointer"
                />
                <button
                  onClick={() => isolateSessionIntent(value)}
                  className={`flex-1 text-left font-mono text-xs hover:text-bear transition-colors cursor-pointer py-0.5 flex items-center gap-1.5 ${text}`}
                >
                  <span className={`w-2 h-2 rounded-full shrink-0 ${dot}`} />
                  {label}
                </button>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-stone-400">Trigger</span>
            {filterTrigger.size < 4 && (
              <button
                onClick={() => setFilterTrigger(new Set([TriggerSourceFilter.Button, TriggerSourceFilter.Weight, TriggerSourceFilter.Api, TriggerSourceFilter.None]))}
                className="font-mono text-[10px] text-stone-400 hover:text-bear cursor-pointer"
              >
                Clear
              </button>
            )}
          </div>
          <div className="space-y-0.5">
            {([
              { label: 'Weight', value: TriggerSourceFilter.Weight, count: weightCount,    dot: 'bg-gold',      text: 'text-gold font-medium' },
              { label: 'Button', value: TriggerSourceFilter.Button, count: buttonCount,    dot: 'bg-stone-500', text: 'text-stone-700 font-medium' },
              { label: 'API',    value: TriggerSourceFilter.Api,    count: apiCount,       dot: 'bg-pine',      text: 'text-pine font-medium' },
              { label: 'None',   value: TriggerSourceFilter.None,   count: noTriggerCount, dot: 'bg-stone-300', text: 'text-stone-500' },
            ] as const).map(({ label, value, count, dot, text }) => (
              <div key={value} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={filterTrigger.has(value)}
                  onChange={() => toggleTrigger(value)}
                  className="w-3.5 h-3.5 rounded border-stone-300 accent-bear cursor-pointer"
                />
                <button
                  onClick={() => isolateTrigger(value)}
                  className={`flex-1 text-left font-mono text-xs hover:text-bear transition-colors cursor-pointer py-0.5 flex items-center gap-1.5 ${text}`}
                >
                  <span className={`w-2 h-2 rounded-full shrink-0 ${dot}`} />
                  {label}
                </button>
                <span className="font-mono text-[10px] text-stone-400">{count}</span>
              </div>
            ))}
          </div>
        </div>

        {labelOptions.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-stone-400">Labels</span>
              {filterLabels.size > 0 && (
                <button
                  onClick={() => setFilterLabels(new Set())}
                  className="font-mono text-[10px] text-stone-400 hover:text-bear cursor-pointer"
                >
                  Clear
                </button>
              )}
            </div>
            {/* Any-of: a capture shows if it carries at least one ticked label. */}
            <div className="max-h-56 overflow-y-auto space-y-1.5 pr-1">
              {([CaptureLabelKind.Catch, CaptureLabelKind.Length, CaptureLabelKind.Weight] as const).map(kind => {
                const ofKind = labelOptions.filter(l => l.kind === kind)
                if (ofKind.length === 0) return null
                return (
                  <div key={kind}>
                    <span className="font-mono text-[9px] tracking-[0.15em] uppercase text-stone-400">{kind}</span>
                    <div className="space-y-0.5">
                      {ofKind.map(l => (
                        <label key={l.id} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={filterLabels.has(l.id)}
                            onChange={() => toggleLabel(l.id)}
                            className="w-3.5 h-3.5 rounded border-stone-300 accent-bear cursor-pointer"
                          />
                          <span className="flex-1 font-mono text-xs text-stone-700 py-0.5 truncate" title={l.description ?? undefined}>
                            {l.category ? <span className="text-stone-400">{l.category} · </span> : null}{l.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    )
  }

  const thumbCls = compact ? 'w-10 h-10' : 'w-24 h-24'

  return (
    <div className="min-h-screen bg-stone-50">
      <AppHeader
        active="captures"
        onNavigate={onNavigate}
        onLogout={onLogout}
        onLogoClick={handlePanelClose}
        containerClassName=""
        actions={<>
          <button
            onClick={() => setFilterDrawerOpen(true)}
            className="min-[600px]:hidden font-mono text-[10px] tracking-[0.12em] uppercase text-stone-600 border border-stone-200
              px-2.5 py-1.5 rounded-lg hover:border-stone-400 transition-colors cursor-pointer"
          >
            Filter
          </button>
          <button
            onClick={() => loadCaptures()}
            disabled={loadingCaptures}
            className="min-[600px]:hidden font-mono text-[10px] tracking-[0.12em] uppercase text-stone-600 border border-stone-200
              px-2.5 py-1.5 rounded-lg hover:border-stone-400 transition-colors cursor-pointer disabled:opacity-50"
          >
            Refresh
          </button>
          <button
            onClick={() => setShowLogCapture(true)}
            className="hidden min-[600px]:flex items-center gap-1.5 font-mono text-[10px] tracking-[0.12em] uppercase bg-bear text-white
              px-3 py-1.5 rounded-lg hover:bg-bear-light transition-colors cursor-pointer"
          >
            + Log Capture
          </button>
        </>}
      />

      <div className="flex" style={{ height: 'calc(100vh - 57px)' }}>

        {/* Desktop filter sidebar — collapsible */}
        <aside
          className={`hidden min-[600px]:flex flex-col shrink-0 border-r border-stone-200 bg-white overflow-hidden transition-all duration-200 ${sidebarCollapsed ? 'w-10' : 'w-56'}`}
        >
          <div className={`flex ${sidebarCollapsed ? 'justify-center pt-3' : 'justify-end p-2'}`}>
            <button
              onClick={() => setSidebarCollapsed(v => !v)}
              className="p-1.5 rounded-lg hover:bg-stone-100 text-stone-400 hover:text-stone-700 transition-colors cursor-pointer"
              title={sidebarCollapsed ? 'Show filters' : 'Hide filters'}
            >
              {sidebarCollapsed ? (
                <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <path d="M6 4l4 4-4 4" />
                </svg>
              ) : (
                <svg viewBox="0 0 16 16" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <path d="M10 4l-4 4 4 4" />
                </svg>
              )}
            </button>
          </div>
          {!sidebarCollapsed && (
            <div className="flex-1 overflow-y-auto px-4 pb-4">
              {FilterPanelContent()}
            </div>
          )}
        </aside>

        {/* Main content */}
        <div
          className="flex-1 min-w-0 overflow-y-auto transition-all duration-300"
          style={{ marginRight: selectedCapture ? 420 : 0 }}
        >
          {/* Bulk action bar — desktop only, shown when selection non-empty */}
          {selected.size > 0 && (
            <div className="hidden min-[600px]:flex items-center gap-3 px-4 py-2 bg-bear/5 border-b border-bear/20">
              <span className="font-mono text-[11px] text-stone-700">{selected.size} selected</span>
              <button
                onClick={() => setBulkConfirm({ action: 'keep' })}
                className="font-mono text-[10px] tracking-[0.12em] uppercase bg-pine text-white px-3 py-1 rounded-lg hover:opacity-90 transition-opacity cursor-pointer"
              >
                Keep
              </button>
              <button
                onClick={() => setBulkConfirm({ action: 'discard' })}
                className="font-mono text-[10px] tracking-[0.12em] uppercase bg-coral text-white px-3 py-1 rounded-lg hover:opacity-90 transition-opacity cursor-pointer"
              >
                Discard
              </button>
              {catchLabels.length > 0 && (
                <>
                  <span className="w-px h-5 bg-bear/20 mx-1" />
                  <span className="font-mono text-[10px] tracking-[0.12em] uppercase text-stone-500">Catch label</span>
                  <select
                    value={bulkLabelId}
                    onChange={e => setBulkLabelId(e.target.value)}
                    className="font-mono text-[11px] border border-stone-300 rounded-lg px-2 py-1 bg-white text-stone-700"
                  >
                    <option value="">choose…</option>
                    {catchLabels.map(l => <option key={l.id} value={l.id}>{labelText(l)}</option>)}
                  </select>
                  <button
                    disabled={!bulkLabelId}
                    onClick={() => setBulkLabelConfirm({ labelId: bulkLabelId, applied: true })}
                    className="font-mono text-[10px] tracking-[0.12em] uppercase bg-ink-500 text-white px-3 py-1 rounded-lg hover:opacity-90 transition-opacity cursor-pointer disabled:opacity-40 disabled:cursor-default"
                  >
                    Add
                  </button>
                  <button
                    disabled={!bulkLabelId}
                    onClick={() => setBulkLabelConfirm({ labelId: bulkLabelId, applied: false })}
                    className="font-mono text-[10px] tracking-[0.12em] uppercase text-ink-500 border border-ink-200 px-3 py-1 rounded-lg hover:border-ink-400 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-default"
                  >
                    Remove
                  </button>
                </>
              )}
              <button
                onClick={() => setSelected(new Set())}
                className="font-mono text-[10px] tracking-[0.12em] uppercase text-stone-500 hover:text-stone-900 px-2 py-1 cursor-pointer"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Table toolbar */}
          <div className="hidden min-[600px]:flex items-center justify-between px-4 py-2 border-b border-stone-100 bg-white">
            <p className="font-mono text-[10px] text-stone-400">
              {displayedCaptures.length} capture{displayedCaptures.length !== 1 ? 's' : ''}
              {listTruncated && ' · showing the newest 250 — narrow by date to see older'}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => loadCaptures()}
                disabled={loadingCaptures}
                className="font-mono text-[10px] tracking-[0.12em] uppercase text-stone-500 hover:text-stone-900
                  border border-stone-200 px-2 py-1 rounded hover:border-stone-400 transition-colors cursor-pointer disabled:opacity-50"
              >
                Refresh
              </button>
              <button
                onClick={() => setCompact(v => !v)}
                className="font-mono text-[10px] tracking-[0.12em] uppercase text-stone-500 hover:text-stone-900
                  border border-stone-200 px-2 py-1 rounded hover:border-stone-400 transition-colors cursor-pointer"
              >
                {compact ? 'Expand rows' : 'Compact rows'}
              </button>
            </div>
          </div>

          {/* Desktop table */}
          <div className="hidden min-[600px]:block">
            {loadingCaptures ? (
              <div className="flex items-center justify-center py-24">
                <p className="font-mono text-xs tracking-[0.2em] uppercase text-stone-400 animate-pulse">Loading…</p>
              </div>
            ) : displayedCaptures.length === 0 ? (
              <div className="flex items-center justify-center py-24">
                <p className="font-mono text-xs tracking-[0.2em] uppercase text-stone-400">No captures</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[620px] border-collapse">
                  <thead>
                    <tr className="bg-stone-50 border-b border-stone-200">
                      <th className="pl-3 pr-1 py-2.5 w-8" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={displayedCaptures.length > 0 && displayedCaptures.every(c => selected.has(c.captureId))}
                          onChange={toggleSelectAll}
                          className="w-3.5 h-3.5 rounded border-stone-300 accent-bear cursor-pointer"
                          title="Select all"
                        />
                      </th>
                      <th className="px-4 py-2.5 w-8" />
                      <SortHeader label="Code"     k="triage"   className="w-24" />
                      <th className="px-3 py-2.5 font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500 text-left">Photo</th>
                      <SortHeader label="Date"     k="date" />
                      <SortHeader label="Net"      k="net" />
                      <th className="px-3 py-2.5 font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500 text-left w-20">Trigger</th>
                      <SortHeader label="Weight"   k="weight"   className="w-20" />
                      <SortHeader label="Length"   k="length"   className="w-20" />
                      <th className="px-3 py-2.5 font-mono text-[10px] tracking-[0.15em] uppercase text-stone-500 text-left w-32">Species</th>
                      <SortHeader label="Status"   k="triage"   className="w-36" />
                    </tr>
                  </thead>
                  <tbody>
                    {displayedCaptures.map(c => {
                      const isManual = c.source === CaptureSource.Manual
                      return (
                        <tr
                          key={c.captureId}
                          onClick={() => handleRowClick(c.captureId)}
                          className={`border-b border-stone-100 cursor-pointer transition-colors
                            ${c.captureId === selectedCaptureId ? 'bg-bear/5' : isManual ? 'bg-stone-50 hover:bg-stone-100' : 'bg-white hover:bg-stone-50'}`}
                        >
                          <td className="pl-3 pr-1 py-2" onClick={e => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selected.has(c.captureId)}
                              onChange={() => toggleSelectOne(c.captureId)}
                              className="w-3.5 h-3.5 rounded border-stone-300 accent-bear cursor-pointer"
                            />
                          </td>
                          <td className="pl-3 pr-1 py-2">
                            {isManual && (
                              <span
                                title="Capture was logged manually"
                                className="font-mono text-[8px] tracking-wider uppercase text-stone-400 bg-stone-200 px-1 py-0.5 rounded cursor-default"
                              >M</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5">
                            <span className="font-mono text-[11px] text-stone-700 leading-tight">{displayCodeStr(c)}</span>
                          </td>
                          <td className="px-3 py-2">
                            <div className={`${thumbCls} shrink-0 rounded overflow-hidden bg-stone-100 transition-all duration-200 flex items-center justify-center`}>
                              {c.thumbnailUrl
                                ? <img src={c.thumbnailUrl} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                                : isManual ? <span className="font-mono text-[8px] text-stone-400">M</span> : null
                              }
                            </div>
                          </td>
                          <td className="px-3 py-2.5 font-mono text-[11px] text-stone-700 whitespace-nowrap">
                            {fmtDate(captureTime(c))}
                          </td>
                          <td className="px-3 py-2.5 font-mono text-[11px] text-stone-700">
                            {c.hostname ?? <span className="text-stone-400 italic">manual</span>}
                          </td>
                          <td className="px-3 py-2.5">
                            <TriggerChip source={c.triggerSource} />
                          </td>
                          <td className="px-3 py-2.5 font-mono text-[11px] text-stone-700">
                            <ComparisonCell
                              rows={[
                                { label: 'guide', v: guideNum(c, 'measured_weight_grams') },
                                { label: 'net', v: c.weightGrams },
                              ]}
                              fmt={g => `${(g * LB_PER_GRAM).toFixed(1)} lb`}
                            />
                          </td>
                          <td className="px-3 py-2.5 font-mono text-[11px] text-stone-700">
                            <ComparisonCell
                              rows={[
                                { label: 'guide', v: guideNum(c, 'measured_length_mm') },
                                { label: 'fishial', v: c.serverLengthMillimeters ?? null },
                                { label: 'net', v: c.lengthMillimeters },
                              ]}
                              fmt={mm => `${(mm * IN_PER_MM).toFixed(1)} in`}
                            />
                          </td>
                          <td className="px-3 py-2.5 font-mono text-[11px]">
                            {/* Human annotation species wins; else Fishial top-1 (muted, italic; dimmer when low-confidence). */}
                            {c.annotation?.species
                              ? <span className="text-stone-700">{String(c.annotation.species)}</span>
                              : c.speciesResult && c.speciesResult.predictions.length > 0
                                ? <span
                                    title={`Fishial: ${c.speciesResult.predictions[0].name} · ${Math.round((c.speciesResult.confidence ?? 0) * 100)}%`}
                                    className={`italic ${(c.speciesResult.confidence ?? 0) < 0.5 ? 'text-stone-400' : 'text-stone-500'}`}
                                  >{commonName(c.speciesResult.predictions[0].name) ?? c.speciesResult.predictions[0].name}</span>
                                : <span className="text-stone-400">—</span>}
                          </td>
                          <td className="px-3 py-2" onClick={e => e.stopPropagation()}>
                            <select
                              value={c.triageResult}
                              onChange={e => handleTriageChange(c.captureId, e.target.value as TriageResult)}
                              className={`font-mono text-[11px] border rounded px-2 py-1 cursor-pointer
                                focus:outline-none focus:ring-1 focus:ring-bear/30 ${triageSelectClass(c.triageResult)}`}
                            >
                              <option value={TriageResult.Pending}>Untriaged</option>
                              <option value={TriageResult.Keep}>Keep</option>
                              <option value={TriageResult.Discard}>Discard</option>
                            </select>
                            {/* catch labels, so bulk changes show in the table */}
                            {(c.labels ?? []).some(l => l.kind === CaptureLabelKind.Catch) && (
                              <div className="flex flex-wrap gap-1 mt-1 max-w-[220px]">
                                {(c.labels ?? []).filter(l => l.kind === CaptureLabelKind.Catch).map(l => (
                                  <span key={l.id} title={l.description ?? undefined}
                                    className="font-mono text-[9.5px] px-1.5 rounded border border-ink-200/60 bg-white text-ink-500 whitespace-nowrap">
                                    {labelText(l)}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Mobile card feed */}
          <div className="min-[600px]:hidden px-3 py-3">
            {listTruncated && !loadingCaptures && (
              <p className="font-mono text-[10px] text-stone-400 mb-2">Showing the newest 250 — narrow by date to see older</p>
            )}
            {loadingCaptures ? (
              <div className="flex items-center justify-center py-16">
                <p className="font-mono text-xs tracking-[0.2em] uppercase text-stone-400 animate-pulse">Loading…</p>
              </div>
            ) : displayedCaptures.length === 0 ? (
              <div className="flex items-center justify-center py-16">
                <p className="font-mono text-xs tracking-[0.2em] uppercase text-stone-400">No captures</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-3">
                {displayedCaptures.map(c => {
                  const isManual = c.source === CaptureSource.Manual
                  const nickname = annNicknameStr(c)
                  const isPending = c.triageResult === TriageResult.Pending
                  return (
                    <button
                      key={c.captureId}
                      onClick={() => handleRowClick(c.captureId)}
                      className="w-full text-left rounded-2xl overflow-hidden bg-white border border-stone-200 hover:shadow-md transition-shadow cursor-pointer"
                    >
                      {/* Photo — full width, fixed aspect ratio */}
                      <div className="w-full bg-stone-900" style={{ aspectRatio: '4/3' }}>
                        {c.thumbnailUrl ? (
                          <img src={c.thumbnailUrl} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="font-mono text-[10px] uppercase tracking-widest text-stone-500">
                              {isManual ? 'Manual' : 'No photo'}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="px-3 py-2.5 space-y-1">
                        {/* Triage badge */}
                        <div>
                          {isPending ? (
                            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg bg-amber-50 border border-amber-200">
                              <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                              <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-amber-700">Untriaged</span>
                            </span>
                          ) : c.triageResult === TriageResult.Keep ? (
                            <span className="inline-block font-mono text-[9px] px-1.5 py-0.5 rounded bg-pine/10 text-pine">Keep</span>
                          ) : (
                            <span className="inline-block font-mono text-[9px] px-1.5 py-0.5 rounded bg-coral/10 text-coral">Discard</span>
                          )}
                        </div>
                        <p className="font-mono font-semibold text-xs text-stone-900 leading-tight">
                          {nickname || displayCodeStr(c)}
                        </p>
                        <p className="font-mono text-[11px] text-stone-500">
                          {c.hostname ?? <span className="italic text-stone-400">manual</span>}
                        </p>
                        <p className="font-mono text-[11px] text-stone-400">{fmtDate(captureTime(c))}</p>
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Side panel */}
      {selectedCapture && (
        <CaptureSidePanel
          mode={panelMode}
          onModeChange={setPanelMode}
          captureItem={selectedCapture}
          detail={details[selectedCapture.captureId] ?? null}
          onClose={handlePanelClose}
          onTriageChange={handleTriageChange}
          onAnnotationSave={handleAnnotationSave}
          onMediaUploaded={handleMediaUploaded}
          onLinked={handleLinked}
          onLabelsChange={(captureId, labels) => patchCapture(captureId, { labels })}
          open={!!selectedCapture}
          onPrev={handlePrev}
          onNext={handleNext}
          prevDisabled={prevDisabled}
          nextDisabled={nextDisabled}
        />
      )}

      {/* Mobile FAB */}
      <button
        onClick={() => setShowLogCapture(true)}
        className="min-[600px]:hidden fixed bottom-6 right-6 z-20 bg-bear text-white rounded-full w-14 h-14 shadow-lg
          flex items-center justify-center hover:bg-bear-light transition-colors cursor-pointer text-2xl"
        aria-label="Log Capture"
      >
        +
      </button>

      {/* Bulk triage confirm modal */}
      <Dialog open={bulkConfirm !== null} onClose={() => !bulkLoading && setBulkConfirm(null)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-stone-200 shadow-xl max-w-sm w-full p-6 space-y-4">
            <h2 className="font-mono text-sm font-semibold text-stone-900">
              {bulkConfirm?.action === 'keep' ? `Keep ${selected.size} item${selected.size !== 1 ? 's' : ''}?` : `Discard ${selected.size} item${selected.size !== 1 ? 's' : ''}?`}
            </h2>
            <p className="font-mono text-[11px] text-stone-500">
              {bulkConfirm?.action === 'keep'
                ? 'These captures will be marked as Keep.'
                : 'These captures will be marked as Discard.'}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setBulkConfirm(null)}
                disabled={bulkLoading}
                className="font-mono text-[10px] tracking-[0.12em] uppercase text-stone-500 hover:text-stone-900 px-3 py-1.5 rounded-lg border border-stone-200 hover:border-stone-400 transition-colors cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => bulkConfirm && executeBulkTriage(bulkConfirm.action)}
                disabled={bulkLoading}
                className={`font-mono text-[10px] tracking-[0.12em] uppercase text-white px-3 py-1.5 rounded-lg transition-opacity cursor-pointer disabled:opacity-50 ${bulkConfirm?.action === 'keep' ? 'bg-pine hover:opacity-90' : 'bg-coral hover:opacity-90'}`}
              >
                {bulkLoading ? 'Saving…' : bulkConfirm?.action === 'keep' ? 'Keep' : 'Discard'}
              </button>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Bulk catch label confirm modal */}
      <Dialog open={bulkLabelConfirm !== null} onClose={() => !bulkLoading && setBulkLabelConfirm(null)} className="relative z-50">
        <div className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-stone-200 shadow-xl max-w-sm w-full p-6 space-y-4">
            <h2 className="font-mono text-sm font-semibold text-stone-900">
              {bulkLabelConfirm?.applied ? 'Add' : 'Remove'} “{bulkLabel ? labelText(bulkLabel) : ''}” {bulkLabelConfirm?.applied ? 'to' : 'from'}{' '}
              {selected.size} item{selected.size !== 1 ? 's' : ''}?
            </h2>
            <p className="font-mono text-[11px] text-stone-500">
              {bulkLabelConfirm?.applied
                ? 'Captures that already have this catch label keep it.'
                : 'Captures without this catch label are left as they are.'}
            </p>
            {bulkLabelError && <p className="font-mono text-[11px] text-rose-700">{bulkLabelError}</p>}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setBulkLabelConfirm(null)}
                disabled={bulkLoading}
                className="font-mono text-[10px] tracking-[0.12em] uppercase text-stone-500 hover:text-stone-900 px-3 py-1.5 rounded-lg border border-stone-200 hover:border-stone-400 transition-colors cursor-pointer disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => bulkLabelConfirm && executeBulkLabel(bulkLabelConfirm.labelId, bulkLabelConfirm.applied)}
                disabled={bulkLoading}
                className="font-mono text-[10px] tracking-[0.12em] uppercase text-white px-3 py-1.5 rounded-lg transition-opacity cursor-pointer disabled:opacity-50 bg-ink-500 hover:opacity-90"
              >
                {bulkLoading ? 'Saving…' : bulkLabelConfirm?.applied ? 'Add label' : 'Remove label'}
              </button>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Mobile filter drawer */}
      <Dialog open={filterDrawerOpen} onClose={() => setFilterDrawerOpen(false)} className="relative z-40 min-[600px]:hidden">
        <div className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-x-0 bottom-0 max-h-[80vh] bg-white rounded-t-2xl flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200">
            <h2 className="font-mono text-sm font-semibold text-stone-900">Filters</h2>
            <button onClick={() => setFilterDrawerOpen(false)}
              className="font-mono text-[10px] tracking-widest uppercase text-stone-500 hover:text-stone-900 cursor-pointer px-2 py-1">
              Done
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {FilterPanelContent()}
          </div>
        </div>
      </Dialog>

      {/* Log Capture modal */}
      <Dialog open={showLogCapture} onClose={() => setShowLogCapture(false)} className="relative z-40">
        <div className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 flex items-end min-[600px]:items-center justify-center min-[600px]:p-4">
          <div className="bg-white w-full min-[600px]:max-w-md min-[600px]:rounded-2xl rounded-t-2xl border border-stone-200 shadow-xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-stone-200 shrink-0">
              <h2 className="font-mono text-sm font-semibold text-stone-900">Log Capture</h2>
              <button onClick={() => setShowLogCapture(false)} className="text-stone-400 hover:text-stone-900 cursor-pointer px-1">✕</button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <LogFormField label="Nickname">
                <input type="text" value={logForm.nickname} onChange={e => setLogForm(f => ({ ...f, nickname: e.target.value }))} className="form-input-cls" placeholder="e.g. Big Bob" />
              </LogFormField>
              <LogFormField label="Net">
                <select value={logForm.selectedHostname} onChange={e => setLogForm(f => ({ ...f, selectedHostname: e.target.value }))} className="form-input-cls">
                  <option value="">— any —</option>
                  {devices.map(d => <option key={d.hostname} value={d.hostname}>{d.hostname}</option>)}
                </select>
              </LogFormField>
              <LogFormField label="Species">
                <input type="text" value={logForm.species} onChange={e => setLogForm(f => ({ ...f, species: e.target.value }))} className="form-input-cls" />
              </LogFormField>
              <div className="grid grid-cols-2 gap-3">
                <LogFormField label="Weight (lbs)">
                  <input type="number" step="0.01" value={logForm.weightLbs} onChange={e => setLogForm(f => ({ ...f, weightLbs: e.target.value }))} className="form-input-cls" placeholder="0.00" />
                </LogFormField>
                <LogFormField label="Length (in)">
                  <input type="number" step="0.1" value={logForm.lengthIn} onChange={e => setLogForm(f => ({ ...f, lengthIn: e.target.value }))} className="form-input-cls" placeholder="0.0" />
                </LogFormField>
              </div>
              <LogFormField label="Angler">
                <input type="text" value={logForm.angler} onChange={e => setLogForm(f => ({ ...f, angler: e.target.value }))} className="form-input-cls" />
              </LogFormField>
              <LogFormField label="Guide">
                <input type="text" value={logForm.guide} onChange={e => setLogForm(f => ({ ...f, guide: e.target.value }))} className="form-input-cls" />
              </LogFormField>
              <LogFormField label="Notes">
                <textarea value={logForm.notes} onChange={e => setLogForm(f => ({ ...f, notes: e.target.value }))} className="form-input-cls min-h-[72px] resize-none" rows={3} />
              </LogFormField>
              <LogFormField label="Time">
                <input type="datetime-local" value={logForm.time} onChange={e => setLogForm(f => ({ ...f, time: e.target.value }))} className="form-input-cls" />
              </LogFormField>
            </div>
            <div className="px-4 py-3 border-t border-stone-200 shrink-0">
              <button onClick={handleLogCapture} disabled={loggingCapture}
                className="w-full py-2.5 rounded-xl bg-bear text-white font-mono text-[11px] tracking-[0.15em] uppercase hover:bg-bear-light transition-colors cursor-pointer disabled:opacity-50">
                {loggingCapture ? 'Logging…' : 'Log capture'}
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  )
}

function LogFormField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block font-mono text-[9px] tracking-[0.2em] uppercase text-stone-400 mb-1">{label}</label>
      {children}
    </div>
  )
}
